// A coordinate inventory (inventar de coordonate) pasted as text.
//
// In Romanian surveying X is the **northing** and Y the **easting**, the
// reverse of CAD's x/y. An inventory from a PAD lists "Nr. pct., X [m],
// Y [m]", so by default the first coordinate is north. On a plan, x is east,
// so the two swap on the way in. Getting this wrong mirrors the plot across
// its diagonal: the area is right, every edge is the wrong edge, and the
// street ends up where the neighbour was.
import type { Pt, Ring } from "../geom.ts";

export type AxisOrder = "north-east" | "east-north";

export interface ParsedCoords {
  ring: Ring; // plan coordinates: [east, north]
  ids: string[];
  skipped: number[]; // 1-based line numbers that held no coordinate pair
  warnings: string[];
}

/** Stereo 70 covers Romania with northings ~200–800 km and eastings ~100–900 km. */
const inStereo70 = (north: number, east: number) => north > 150_000 && north < 850_000 && east > 50_000 && east < 950_000;

function numbers(line: string): { id?: string; values: number[] } {
  // tabs or semicolons separate columns, so a comma there is a decimal mark;
  // otherwise "325123,45" is a decimal comma and ", " a separator
  const cells = /[\t;]/.test(line)
    ? line.split(/[\t;]+/)
    : line.split(/\s+|,(?=\s)|,(?=-?\d+[.]\d)/);
  const values: number[] = [];
  let id: string | undefined;
  for (const raw of cells) {
    const c = raw.trim().replace(/,$/, "");
    if (!c) continue;
    const normal = /^-?\d+,\d+$/.test(c) ? c.replace(",", ".") : c;
    const n = Number(normal);
    if (Number.isFinite(n)) values.push(n);
    else if (values.length === 0 && id === undefined) id = c;
  }
  // a leading small integer before two coordinates is the point number
  if (values.length >= 3 && Number.isInteger(values[0]) && Math.abs(values[0]) < 100_000) {
    id ??= String(values[0]);
    values.shift();
  }
  return { id, values };
}

export function parseCoordinates(text: string, order: AxisOrder = "north-east"): ParsedCoords {
  const ring: Ring = [];
  const ids: string[] = [];
  const skipped: number[] = [];
  const warnings: string[] = [];
  text.split(/\r\n?|\n/).forEach((line, k) => {
    if (!line.trim()) return;
    const { id, values } = numbers(line);
    if (values.length < 2) { skipped.push(k + 1); return; }
    const [first, second] = values.slice(-2);
    const p: Pt = order === "north-east" ? [second, first] : [first, second];
    ring.push(p);
    ids.push(id ?? String(ring.length));
  });
  if (ring.length > 3) {
    const [a, z] = [ring[0], ring[ring.length - 1]];
    if (Math.abs(a[0] - z[0]) < 0.005 && Math.abs(a[1] - z[1]) < 0.005) { ring.pop(); ids.pop(); }
  }
  if (ring.length && !ring.every(([e, n]) => inStereo70(n, e))) {
    const swapped = ring.every(([e, n]) => inStereo70(e, n));
    warnings.push(swapped
      ? "Coordonatele par să fie în ordinea inversă (Est, Nord). Verifică ordinea axelor."
      : "Coordonatele nu sunt în domeniul Stereo 70. Le folosesc ca atare, în metri.");
  }
  if (ring.length < 3) warnings.push("Un contur are nevoie de cel puțin 3 puncte.");
  return { ring, ids, skipped, warnings };
}
